import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import PreviewTable from "./PreviewTable";
import { API, MappingEditor, PhaseLockControl, Stat, Section, FileUpload } from "../App";

function Phase1({ session, refresh }) {
  const [busy, setBusy] = useState(false);
  const result = session.phase_results?.phase1;
  const locked = !!(session.locked_phases || {}).phase1;
  const complete = !!(result && result.ok);

  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/audit/session/${session.id}/phase1`);
      if (res.data && res.data.ok === false) throw new Error(res.data.error || "Phase 1 backend error");
      toast.success("Phase 1 complete");
      refresh();
    } catch (e) {
      toast.error(`Phase 1 failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <MappingEditor session={session} role="master" onSaved={refresh} />
      <div className="flex flex-wrap items-center gap-3">
        <Button
          data-testid="run-phase1-btn"
          onClick={run}
          disabled={busy || locked}
          title={locked ? "Phase 1 is locked. Unlock to re-run." : ""}
        >
          {busy ? "Running..." : "Run Phase 1"}
        </Button>
        <PhaseLockControl
          session={session}
          phaseKey="phase1"
          complete={complete}
          refresh={refresh}
        />
      </div>
      {result && result.ok && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total Rows" value={result.total_rows} />
            <Stat label="Unique Booking IDs" value={result.unique_booking_ids} />
            <Stat
              label="Duplicate IDs"
              value={result.duplicate_booking_ids?.length || 0}
              danger={result.duplicate_booking_ids?.length > 0}
            />
            <Stat
              label="Null IDs"
              value={result.null_booking_ids?.length || 0}
              danger={result.null_booking_ids?.length > 0}
            />
          </div>
          <Section title="Status value counts">
            <pre className="bg-muted p-2 rounded text-xs">
              {JSON.stringify(result.status_value_counts, null, 2)}
            </pre>
          </Section>
          <Section title="Source value counts">
            <pre className="bg-muted p-2 rounded text-xs">
              {JSON.stringify(result.source_value_counts, null, 2)}
            </pre>
          </Section>
          <Section title={`Non-zero Balance (${result.non_zero_balance?.length || 0})`}>
            <PreviewTable session={session} rows={result.non_zero_balance} />
          </Section>
          <Section title={`Negative Balance (${result.negative_balance?.length || 0})`}>
            <PreviewTable session={session} rows={result.negative_balance} />
          </Section>
          <Section title={`Invalid Status (${result.invalid_status?.length || 0})`}>
            <PreviewTable session={session} rows={result.invalid_status} />
          </Section>
          <Section
            title={`OTA Charged Cancellations (${result.ota_charged_cancellations?.length || 0})`}
          >
            <PreviewTable session={session} rows={result.ota_charged_cancellations} />
          </Section>
        </div>
      )}
      {result && !result.ok && (
        <p className="text-destructive text-sm">{result.error}</p>
      )}
    </div>
  );
}


export default Phase1;
